import { useCallback, useEffect, useMemo, useState } from 'react';
import { isUndefined } from 'lodash-es';
// eslint-disable-next-line @alf/loop-deps
import { bonusesActions } from '@namespace/bonuses';
import {
  getBsActiveDepositBonuses,
  getFreeSpinBonusesList,
  getFundsBonusesList
} from '@namespace/bonuses/src/api';
import {
  ACCENT_COLOR_TYPE_BY_BONUS,
  BONUS_SLIDER_BONUS_TYPES,
  BONUSES_TYPES_FOR_IMAGES,
  freeSpinStates,
  freeSpinStatesMap,
  fundsStates,
  fundsStatesMap,
  PREWAGER_BONUS_TYPE
} from '@namespace/bonuses/src/constants';
import { FORM_FACTORS_MAP, useDeviceStore } from '@namespace/device';
import {
  LOCAL_STORAGE_KEYS,
  setLocalStorageValue,
  useDidUpdateEffect
} from '@namespace/helpers';
import { useI18n } from '@namespace/i18n';
import { bulletSubscribe, bulletUnsubscribe } from '@namespace/socket';

const { FETCH_BONUS_TEMPLATES_IMAGES } = bonusesActions;

// TODO: removed from forms ??
const useDepositBonuses = ({
  isPreWagerBonusesEnabled,
  isPreWagerDepositFundsEnabled,
  isNewBonusImagesEnabled,
  bettingCurrency
}) => {
  const [bonusSystemBonuses, setBonusSystemBonuses] = useState([]);
  const [freeSpinsBonuses, setFreeSpinsBonuses] = useState([]);
  const [fundsBonuses, setFundsBonuses] = useState([]);
  const { isDesktop } = useDeviceStore();
  const platform = isDesktop
    ? FORM_FACTORS_MAP.DESKTOP
    : FORM_FACTORS_MAP.MOBILE;
  const { t } = useI18n();

  const fetchFreeSpinBonuses = useCallback(async () => {
    const { response } = await getFreeSpinBonusesList(
      {
        stateList: freeSpinStatesMap.all,
        pageSize: process.env.BONUSES_COUNT_TO_LOAD || 10,
        currentPage: 1
      },
      platform
    );
    setFreeSpinsBonuses(
      response
        .filter(({ state }) => state === freeSpinStates.FREESPIN_POTENCIAL)
        .map((bonus) => ({
          ...bonus,
          imageColorAccent:
            ACCENT_COLOR_TYPE_BY_BONUS[
              BONUSES_TYPES_FOR_IMAGES.CRM_FREE_SPIN_BONUS
            ],
          type: BONUS_SLIDER_BONUS_TYPES.FREE_SPIN,
          fullBonusTitle: t(
            `content.bonus.template.name.${bonus.bonusModelId}`,
            bonus.freespinName
          )
        }))
    );
  }, [platform]);

  const fetchBonuses = useCallback(
    async ({ disableCRMBonusesLoad = false } = {}) => {
      const fetchBonusSystemBonuses = async () => {
        const { campaigns } = await getBsActiveDepositBonuses();
        const bsBonuses = campaigns?.map((bonus) => ({
          ...bonus,
          bonusModelId: bonus.campaignId,
          isBsBonus: true,
          type: BONUS_SLIDER_BONUS_TYPES.BONUS_SYSTEM,
          imageColorAccent:
            bonus.bonusType === PREWAGER_BONUS_TYPE
              ? ACCENT_COLOR_TYPE_BY_BONUS[
                  BONUSES_TYPES_FOR_IMAGES.BS_PWB_BONUS
                ]
              : ACCENT_COLOR_TYPE_BY_BONUS[
                  BONUSES_TYPES_FOR_IMAGES.BS_PWS_BONUS
                ],
          fullBonusTitle: t(
            `content.bonus.template.name.bs.${bonus.campaignId}`,
            bonus.campaignName
          )
        }));
        if (bsBonuses) {
          setBonusSystemBonuses(bsBonuses);
        }
        return bsBonuses;
      };

      const fetchFundsBonuses = async () => {
        const { response } = await getFundsBonusesList(
          {
            stateList: fundsStatesMap.all,
            pageSize: 10,
            currentPage: 1
          },
          platform
        );
        setFundsBonuses(
          response
            .filter(({ state }) => state === fundsStates.BONUS_POTENCIAL)
            .map((bonus) => ({
              ...bonus,
              imageColorAccent:
                ACCENT_COLOR_TYPE_BY_BONUS[
                  BONUSES_TYPES_FOR_IMAGES.CRM_FUNDS_BONUS
                ],
              type: bonus?.isCashback
                ? BONUS_SLIDER_BONUS_TYPES.CASHBACK
                : BONUS_SLIDER_BONUS_TYPES.FUNDS,
              fullBonusTitle: t(
                `content.bonus.template.name.${bonus.bonusModelId}`,
                bonus.bonusName
              )
            }))
        );
      };

      if (isPreWagerBonusesEnabled) {
        const bonuses = await fetchBonusSystemBonuses();

        if (bonuses?.length && !disableCRMBonusesLoad) {
          if (isPreWagerDepositFundsEnabled) {
            fetchFundsBonuses();
          }
          fetchFreeSpinBonuses();
        }
      }
    },
    [
      isPreWagerBonusesEnabled,
      isPreWagerDepositFundsEnabled,
      fetchFreeSpinBonuses,
      platform,
      t
    ]
  );

  useEffect(() => {
    if (isNewBonusImagesEnabled) {
      FETCH_BONUS_TEMPLATES_IMAGES();
    }
    fetchBonuses();
  }, []);

  useDidUpdateEffect(() => {
    if (
      bettingCurrency &&
      isPreWagerBonusesEnabled &&
      bonusSystemBonuses.length
    ) {
      fetchFreeSpinBonuses();
    }
  }, [bettingCurrency, isPreWagerBonusesEnabled, fetchFreeSpinBonuses]);

  useEffect(() => {
    bulletSubscribe({
      name: 'balance-bullet-1',
      types: ['deposit'],
      callback: (data) => {
        if (data.action === 'update') {
          fetchBonuses();
        }
      }
    });

    return () => {
      bulletUnsubscribe('balance-bullet-1');
    };
  }, [fetchBonuses]);

  const activeBonusSystemBonusId = useMemo(() => {
    const filteredBonuses = bonusSystemBonuses
      ?.filter(
        ({ isSelectedDeposit, bonusId }) =>
          isSelectedDeposit && !isUndefined(bonusId)
      )
      .map(({ bonusId }) => bonusId);

    return filteredBonuses[0];
  }, [bonusSystemBonuses]);

  useEffect(() => {
    if (activeBonusSystemBonusId) {
      setLocalStorageValue(
        LOCAL_STORAGE_KEYS.BONUS_SLIDER_ACTIVE_BONUS,
        activeBonusSystemBonusId || null
      );
    }
  }, [activeBonusSystemBonusId]);

  return {
    // all possible bonuses list, used for almost all partners (except for casinoua)
    bonuses: [...bonusSystemBonuses, ...fundsBonuses, ...freeSpinsBonuses],
    // selectable / auto selectable bonuses. needed for casinoua partner for now
    bonusesByType: {
      selectableBonuses: [
        {
          bonusName: 'web.personaloffice.deposit.nobonus',
          type: BONUS_SLIDER_BONUS_TYPES.DEFAULT
        },
        ...bonusSystemBonuses
      ],
      autoSelectableBonuses: { freeSpinsBonuses }
    },
    isHasBonusSystemBonuses: bonusSystemBonuses?.length > 0,
    activeBonusSystemBonusId,
    fetchBonuses
  };
};

export default useDepositBonuses;
